import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import type IProjectGroupsCompletionItem from "../dataTypes/IProjectGroupsCompletionItem";

interface EnduserEngagementCardProps {
  parcours: IProjectGroupsCompletionItem[];
}

/**
 * Hero card displaying engagement metrics
 */
const EnduserEngagementCard = ({ parcours }: EnduserEngagementCardProps) => {
  const { t, i18n } = useTranslation();

  const { started, completed, lastActivity, notStarted } = useMemo(() => {
    const startedCount = parcours.filter(
      (p) => Math.round(p.progressPercentageOnLatestBuild) > 0,
    ).length;
    const completedCount = parcours.filter(
      (p) => Math.round(p.progressPercentageOnLatestBuild) === 100,
    ).length;
    const notStartedCount = parcours.filter(
      (p) => Math.round(p.progressPercentageOnLatestBuild) === 0,
    ).length;

    // Find most recent session
    const validDates = parcours
      .filter((p) => p.dateLastSession)
      .map((p) => new Date(p.dateLastSession).getTime());

    const lastActivityDate = validDates.length > 0 ? new Date(Math.max(...validDates)) : null;

    return {
      started: startedCount,
      completed: completedCount,
      lastActivity: lastActivityDate,
      notStarted: notStartedCount,
    };
  }, [parcours]);

  // Format last activity date
  const formattedLastActivity = useMemo(() => {
    if (!lastActivity) return null;
    const locale = i18n.language === "fr" ? fr : undefined;
    return formatDistanceToNow(lastActivity, { addSuffix: true, locale });
  }, [lastActivity, i18n.language]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">
          {t("enduserStats.heroCards.engagement")}
        </h3>
        <div className="p-2 bg-blue-100 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        </div>
      </div>

      {/* Main metric - Parcours completed as primary focus */}
      <div className="mb-3">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-3xl font-bold text-green-600">{completed}</span>
          <span className="text-sm text-gray-500">
            {t("enduserStats.heroCards.parcoursCompletedOf")} {started}{" "}
            {t("enduserStats.heroCards.parcoursStartedShort")}
          </span>
        </div>
        {/* Visual progress bar showing completed/started ratio */}
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 transition-all duration-500"
            style={{ width: started > 0 ? `${(completed / started) * 100}%` : "0%" }}
          />
        </div>
      </div>

      {/* Parcours funnel - clear visual breakdown */}
      <div className="flex items-center gap-3 mb-3 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-gray-600">
            {completed} {t("enduserStats.heroCards.completedShort")}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-yellow-500" />
          <span className="text-gray-600">
            {started - completed} {t("enduserStats.heroCards.inProgressShort")}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-gray-300" />
          <span className="text-gray-600">
            {notStarted} {t("enduserStats.heroCards.notStartedShort")}
          </span>
        </div>
      </div>

      {/* Last activity */}
      <div className="flex items-center gap-2 mt-auto pt-3 border-t border-gray-100">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-blue-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="text-xs text-gray-500">{t("enduserStats.heroCards.lastActivity")}</span>
        <span className="text-sm font-semibold text-gray-900">{formattedLastActivity ?? "-"}</span>
      </div>
    </div>
  );
};

export default EnduserEngagementCard;
